import { useEffect, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  OwnershipType,
  ownershipValueToTitle,
} from '~domains/partner-application/entities/application';
import {
  OwnershipFormValues,
  ownershipShema,
} from '~domains/partner-application/entities/application';
import {
  PARTNER_APPLICATION_TOTAL_STEPS,
  STEP_INDEX_BUSINESS,
} from '~domains/partner-application/shared/constants';

import { FooterWithProgressBar } from '~shared/ui/footer-with-progress-bar';
import { Screen, ScreenHeader } from '~shared/ui/screen';

import { Chips } from '@breezeventures/ui-kit/chips/rhf';
import { Icon } from '@breezeventures/ui-kit/next/icon';
import { Typography } from '@breezeventures/ui-kit/typography';
import { zodResolver } from '@hookform/resolvers/zod';

const ownershipTypes: Array<{
  value: OwnershipType;
  title: string;
  icon: ReactNode;
}> = [
  {
    value: 'INDIVIDUAL',
    title: ownershipValueToTitle['INDIVIDUAL'],
    icon: <Icon name="user" size={24} version="v1" />,
  },
  {
    value: 'PARTNERSHIP',
    title: ownershipValueToTitle['PARTNERSHIP'],
    icon: <Icon name="users" size={24} version="v1" />,
  },
  {
    value: 'CORPORATION',
    title: ownershipValueToTitle['CORPORATION'],
    icon: <Icon name="suitcase" size={24} version="v1" />,
  },
];

type OwnershipProps = {
  initialValues?: OwnershipFormValues;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (values: OwnershipFormValues) => Promise<void>;
};

export const Ownership = ({
  initialValues,
  onBack,
  onClose,
  onSubmit,
}: OwnershipProps) => {
  const methods = useForm<OwnershipFormValues>({
    resolver: zodResolver(ownershipShema),
    mode: 'all',
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (initialValues) {
      methods.trigger();
    }
  }, [initialValues, methods]);

  const handleSubmit = async (values: OwnershipFormValues) => {
    await onSubmit(values);
  };

  return (
    <form
      onSubmit={methods.handleSubmit(handleSubmit)}
      className="flex h-full flex-col"
    >
      <Screen
        header={<ScreenHeader onBack={onBack} onClose={onClose} />}
        footer={
          <FooterWithProgressBar
            currentStepIndex={STEP_INDEX_BUSINESS}
            totalSteps={PARTNER_APPLICATION_TOTAL_STEPS}
            isLoading={methods.formState.isSubmitting}
          />
        }
      >
        <Typography.Title size="s" className="py-6">
          What is your business type?
        </Typography.Title>
        <Controller
          name="type"
          control={methods.control}
          render={(fieldProps) => (
            <>
              <Chips
                {...fieldProps}
                type="single"
                variant="outlined"
                size="l"
                width="full"
                className={`grid grid-cols-2 gap-3`}
              >
                {ownershipTypes.map((option) => {
                  return (
                    <Chips.Item
                      key={option.value}
                      value={option.value}
                      className={`flex h-auto! flex-col items-start p-3!`}
                    >
                      <div
                        className={`
                          mb-9 flex h-10 w-10 items-center justify-center
                          rounded-full bg-accent-1 text-white
                        `}
                      >
                        {option.icon}
                      </div>
                      <Typography.Text size="m" className="text-start">
                        {option.title}
                      </Typography.Text>
                    </Chips.Item>
                  );
                })}
              </Chips>
              <div
                className={`
                  overflow-hidden transition-all duration-300 ease-in-out
                  ${
                    fieldProps.fieldState?.error
                      ? 'h-full opacity-100'
                      : `h-0 opacity-0`
                  }
                `}
              >
                <Typography.Text
                  size="xs"
                  color="negative"
                  className="pt-2 pl-2"
                >
                  {fieldProps.fieldState?.error?.message}
                </Typography.Text>
              </div>
            </>
          )}
        />
      </Screen>
    </form>
  );
};
